import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { Icon, IconInput, IconTile, useEscapeLayer } from '@/design-system';
import { cx } from '@/lib/cx';
import { formatNumber } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { TELEGRAM_MIN_QUERY, telegramChannelsQuery, type TelegramChannel } from './api';
import s from './organizations.module.css';

/** A link or a @handle (`t.me/…`, `@sharq_uz`, `https://…`): what the field stores as typed, never a search. */
export function looksLikeTelegramLink(text: string): boolean {
  return /^@|[/:]|t\.me|telegram\.me/i.test(text.trim());
}

/** The API's 503 for a search switched off (the legal gate or Settings): the field is a plain input again. */
function switchedOff(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'telegram_search_unavailable' && err.body.params.reason === 'disabled';
}

/**
 * The «Публичный Telegram» source input with «find the channel» (additive, CONNECTORS §2 «Telegram over MTProto»).
 * The input is the design's; typing a name rather than a link (`Sharq Textile`) opens a list under it, in the
 * dropdown's language (#151517 card, .08 border, radius 16, the palette's 34px icon-tile rows): the public
 * channels Telegram finds, with the @username, subscribers and the verified mark. Picking one puts its
 * `https://t.me/<username>` in the field. While the search is off (the API's 503 `disabled`) nothing appears;
 * any other failure says so in one line, and the field still takes a pasted link.
 */
export function TelegramSourceField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [off, setOff] = useState(false);
  const text = value.trim();
  const searchable = !off && text.length >= TELEGRAM_MIN_QUERY && !looksLikeTelegramLink(text);
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(searchable ? text : ''), 400);
    return () => clearTimeout(id);
  }, [text, searchable]);
  const res = useQuery({ ...telegramChannelsQuery(q), enabled: searchable && q.length >= TELEGRAM_MIN_QUERY });
  const disabled = switchedOff(res.error);
  useEffect(() => {
    if (disabled) setOff(true);
  }, [disabled]);

  const channels = res.isError ? [] : (res.data?.channels ?? []);
  const show = open && searchable && !disabled && q.length >= TELEGRAM_MIN_QUERY;
  const current = Math.min(active, Math.max(0, channels.length - 1));
  const close = () => setOpen(false);
  useEscapeLayer(show, close);

  const pick = (c: TelegramChannel | undefined) => {
    if (!c) return;
    onChange(c.url);
    close();
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!show || !channels.length) return;
    if (e.key === 'ArrowDown') setActive((i) => (Math.min(i, channels.length - 1) + 1) % channels.length);
    else if (e.key === 'ArrowUp') setActive((i) => (Math.min(i, channels.length - 1) - 1 + channels.length) % channels.length);
    else if (e.key === 'Enter') pick(channels[current]);
    else return;
    e.preventDefault();
  };
  const sub = (c: TelegramChannel) =>
    [
      `@${c.username}`,
      c.kind === 'group' ? t('new.telegram_search.group') : null,
      c.subscribers != null
        ? t(c.kind === 'group' ? 'new.telegram_search.members' : 'new.telegram_search.subscribers', { count: c.subscribers, n: formatNumber(c.subscribers) })
        : null,
    ]
      .filter(Boolean)
      .join(' · ');

  // Nothing yet to show: the list fades in late, so a quick answer (or a quick «off») never flashes it.
  const waiting = !res.isError && !res.data;
  const optionId = (i: number) => `${listId}-${i}`;
  return (
    <div className={s.tgField}>
      <IconInput
        icon="send"
        placeholder={t('new.telegram')}
        aria-label={t('new.telegram')}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={show}
        aria-controls={show ? listId : undefined}
        aria-activedescendant={show && channels.length ? optionId(current) : undefined}
        onChange={(e) => {
          onChange(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onKeyDown={onKey}
      />
      {show && (
        // The list keeps the input focused: a click picks without blurring it first.
        <div className={cx(s.tgMenu, waiting && s.tgMenuLate)} onMouseDown={(e) => e.preventDefault()}>
          <span className={s.tgCaps} aria-hidden>
            {t('new.telegram_search.title')}
          </span>
          <div id={listId} role="listbox" aria-label={t('new.telegram_search.title')} className={s.tgList}>
            {res.isError ? (
              <div className={s.tgNote}>{errorText(res.error, tc)}</div>
            ) : waiting || (res.isFetching && !channels.length) ? (
              <div className={s.tgNote}>{t('new.telegram_search.searching')}</div>
            ) : !channels.length ? (
              <div className={s.tgNote}>{t('new.telegram_search.empty', { q })}</div>
            ) : (
              channels.map((c, i) => (
                <div
                  key={c.username}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === current}
                  className={cx(s.tgRow, i === current && s.tgRowActive)}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(c)}
                >
                  <IconTile icon="send" size={34} iconSize={15} />
                  <div className={s.tgText}>
                    <span className={s.tgTitle}>
                      <span className={s.tgName}>{c.title}</span>
                      {c.verified && <Icon name="badge-check" size={14} className={s.tgVerified} title={t('new.telegram_search.verified')} />}
                    </span>
                    <span className={s.tgSub}>{sub(c)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
